/**
 * CaseTrackUS - Immigration Case Tracking Platform
 * -------------------------------------------------
 * Module: src/pages/Login.js
 * Purpose: Login and registration page.
 *          First page users see when not logged in.
 *          Supports two authentication methods:
 *          - Email and password (Sign In / Register tabs)
 *          - Google OAuth (Continue with Google button)
 *
 * Author: Irina Panferova
 * Company: Panferov LLC
 * Website: https://casetrackus.com
 * Contact: support@casetrackus.com
 * Created: 2025
 * Updated: 2026
 * Copyright: (c) 2026 Panferov LLC. All rights reserved.
 */

import React, { useState } from 'react';
import {
  Container, Box, TextField, Button, Typography,
  Alert, Tab, Tabs, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import { login, register } from '../services/api';
import axios from 'axios';

// Backend URL for Google OAuth redirect
const API_URL = process.env.REACT_APP_API_URL || 'https://casetrackus.com';

/**
 * Login page component.
 *
 * Props:
 *   onLogin (function) — called with user object after successful login
 *                        tells App.js to switch to Dashboard
 */
export default function Login({ onLogin }) {

  // ─────────────────────────────────────
  // State Variables
  // ─────────────────────────────────────
  const [tab, setTab] = useState(0);          // 0 = Sign In, 1 = Register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState('');  // 'individual' or 'professional'
  const [firmName, setFirmName] = useState('');
  const [agreedProfessional, setAgreedProfessional] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');     // error message to display
  const [loading, setLoading] = useState(false); // shows spinner when true
  const [agreed, setAgreed] = useState(false);        // terms agreement checkbox

  // ─────────────────────────────────────
  // Submit Handler
  // ─────────────────────────────────────

  /**
   * Handles login or registration form submission.
   * Calls the appropriate API function based on active tab.
   * Saves token and user to localStorage on success.
   */
  const handleForgot = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMsg('');
    try {
      await axios.post(`${API_URL}/auth/forgot-password?email=${encodeURIComponent(forgotEmail)}`);
      setForgotMsg('✅ If this email is registered, you will receive a reset link shortly.');
    } catch (err) {
      setForgotMsg('✅ If this email is registered, you will receive a reset link shortly.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');       // clear previous error
    setLoading(true);   // show spinner

    try {
      let response;

      if (tab === 0) {
        // Sign In tab — call login API
        response = await login(email, password);
      } else {
        // Register tab — call register API
        response = await register(email, password, accountType === 'professional' ? firmName : fullName, accountType);
      }

      // Save JWT token to browser localStorage
      // Interceptor in api.js will attach this to every request
      localStorage.setItem('token', response.data.access_token);

      // Save user data — JSON.stringify because localStorage only stores strings
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Notify App.js that login was successful — switches to Dashboard
      onLogin(response.data.user);

    } catch (err) {
      // err.response?.data?.detail — safe access to nested error message
      // || 'Something went wrong' — fallback if no detail message
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      // Always hide spinner — even if there was an error
      setLoading(false);
    }
  };

  // ─────────────────────────────────────
  // Render
  // ─────────────────────────────────────

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Container maxWidth="sm">
        <Box sx={{
          background: 'white',
          borderRadius: 3,
          p: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }}>

          {/* App title */}
          <Typography variant="h4" align="center" sx={{ mb: 1, fontWeight: 'bold', color: '#2E7D32' }}>
            🗽 CaseTrackUS
          </Typography>
          <Typography align="center" sx={{ mb: 3, color: 'text.secondary' }}>
            Track your immigration case status
          </Typography>

          {/* Sign In / Register tabs */}
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            centered
            sx={{
              mb: 3,
              '& .MuiTab-root.Mui-selected': { color: '#2E7D32' },
              '& .MuiTabs-indicator': { backgroundColor: '#2E7D32' }
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Register" />
          </Tabs>

          {/* Error message — only shown when error is not empty */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Account Type Selection — only on Register tab */}
          {tab === 1 && !accountType && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, color: '#666', textAlign: 'center' }}>
                Select your account type:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box onClick={() => setAccountType('individual')}
                  sx={{ border: '2px solid #E0E0E0', borderRadius: 2, p: 2.5, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: '#2E7D32', background: '#F1F8E9' } }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>👤</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>Individual</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
                    For immigrants tracking their own cases and family members
                  </Typography>
                </Box>
                <Box onClick={() => setAccountType('professional')}
                  sx={{ border: '2px solid #E0E0E0', borderRadius: 2, p: 2.5, cursor: 'pointer', textAlign: 'center',
                    '&:hover': { borderColor: '#2E7D32', background: '#F1F8E9' } }}>
                  <Typography sx={{ fontSize: '2rem', mb: 1 }}>🏢</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>Professional</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.4 }}>
                    For attorneys, law firms and accredited representatives
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Individual fields */}
          {tab === 1 && accountType === 'individual' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                onClick={() => setAccountType('')}>
                <Typography sx={{ fontSize: '0.8rem', color: '#2E7D32' }}>← Change account type</Typography>
              </Box>
              <Box sx={{ background: '#F1F8E9', borderRadius: 1, p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1.2rem' }}>👤</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#2E7D32', fontWeight: 600 }}>Individual Account</Typography>
              </Box>
              <TextField fullWidth label="Full Name" value={fullName}
                onChange={e => setFullName(e.target.value)} sx={{ mb: 0 }} />
            </Box>
          )}

          {/* Professional fields */}
          {tab === 1 && accountType === 'professional' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
                onClick={() => setAccountType('')}>
                <Typography sx={{ fontSize: '0.8rem', color: '#2E7D32' }}>← Change account type</Typography>
              </Box>
              <Box sx={{ background: '#E8F5E9', borderRadius: 1, p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1.2rem' }}>🏢</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#2E7D32', fontWeight: 600 }}>Professional Account</Typography>
              </Box>
              <TextField fullWidth label="Law Firm / Company Name (or Full Name if solo attorney)"
                value={firmName} onChange={e => setFirmName(e.target.value)} sx={{ mb: 0 }} />
            </Box>
          )}

          {/* Email field — shown on both tabs */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Password field — Enter key also submits the form */}
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            onKeyPress={e => e.key === 'Enter' && handleSubmit()}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )}}
          />

          {/* Forgot password — only on Sign In tab */}
          {tab === 0 && (
            <Box sx={{ textAlign: 'right', mt: -2, mb: 2 }}>
              <Button
                size="small"
                onClick={() => setForgotOpen(true)}
                sx={{ color: '#2E7D32', fontSize: '0.8rem', textTransform: 'none', p: 0 }}
              >
                Forgot password?
              </Button>
            </Box>
          )}

          {/* Terms checkbox — only on Register tab when account type selected */}
          {tab === 1 && accountType && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <input type="checkbox" id="agree" checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, marginRight: 8, cursor: 'pointer', width: 16, height: 16 }} />
                <label htmlFor="agree" style={{ fontSize: 13, color: '#555', cursor: 'pointer', lineHeight: 1.4 }}>
                  I have read and agree to the{' '}
                  <a href="/terms" target="_blank" style={{ color: '#2E7D32' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" style={{ color: '#2E7D32' }}>Privacy Policy</a>
                </label>
              </Box>
              {accountType === 'professional' && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <input type="checkbox" id="agreePro" checked={agreedProfessional}
                    onChange={e => setAgreedProfessional(e.target.checked)}
                    style={{ marginTop: 3, marginRight: 8, cursor: 'pointer', width: 16, height: 16 }} />
                  <label htmlFor="agreePro" style={{ fontSize: 13, color: '#555', cursor: 'pointer', lineHeight: 1.4 }}>
                    I confirm I am a licensed immigration attorney, accredited representative, or authorized law firm employee using this Service in a professional capacity
                  </label>
                </Box>
              )}
            </Box>
          )}

          {/* Submit button — shows spinner when loading */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading || (tab === 1 && (!agreed || !accountType || (accountType === 'professional' && (!firmName || !agreedProfessional))))}
            sx={{
              background: '#2E7D32',
              '&:hover': { background: '#1B5E20' },
              py: 1.5,
              borderRadius: 2
            }}
          >
            {/* Ternary: loading? spinner : tab label */}
            {loading
              ? <CircularProgress size={24} color="inherit" />
              : tab === 0 ? 'Sign In' : 'Create Account'
            }
          </Button>

          {/* Google OAuth button */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              href={`${API_URL}/auth/google`}
              variant="outlined"
              fullWidth
              startIcon={
                // Google logo SVG
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                  <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                  <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                  <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
                </svg>
              }
              sx={{
                mt: 1,
                borderColor: '#ddd',
                color: '#555',
                '&:hover': { borderColor: '#2E7D32', color: '#2E7D32' },
                borderRadius: 2
              }}
            >
              Continue with Google
            </Button>
          </Box>

        </Box>

        {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onClose={() => { setForgotOpen(false); setForgotMsg(''); setForgotEmail(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', pr: 6 }}>
          🔑 Reset Password
          <IconButton onClick={() => { setForgotOpen(false); setForgotMsg(''); setForgotEmail(''); }}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          {forgotMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>{forgotMsg}</Alert>
          )}
          {!forgotMsg && (
            <TextField fullWidth label="Email Address" type="email"
              value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleForgot()}
              sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          {!forgotMsg ? (
            <>
              <Button onClick={() => { setForgotOpen(false); setForgotMsg(''); setForgotEmail(''); }}>Cancel</Button>
              <Button onClick={handleForgot} variant="contained" disabled={forgotLoading || !forgotEmail}
                sx={{ background: '#2E7D32' }}>
                {forgotLoading ? <CircularProgress size={20} /> : 'Send Reset Link'}
              </Button>
            </>
          ) : (
            <Button onClick={() => { setForgotOpen(false); setForgotMsg(''); setForgotEmail(''); }}
              variant="contained" sx={{ background: '#2E7D32' }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Footer links */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <a href="/terms" target="_blank" style={{ color: 'rgba(255,255,255,0.8)', marginRight: 16, fontSize: 13 }}>Terms of Service</a>
          <a href="/privacy" target="_blank" style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Privacy Policy</a>
        </Box>
      </Container>
    </Box>
  );
}
